import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { callMode, mountInline, unmountInline } from "@/lib/ringoverPhone";
import { enterPage } from "@/themes/rapport/motion";
import { LeadAi } from "@/components/pipeline/LeadAi";
import { LeadAttachments } from "@/components/pipeline/LeadAttachments";
import { LeadTickets } from "@/components/pipeline/LeadTickets";
import { timelineItems } from "@/components/pipeline/LeadTimeline";
import { useAuth } from "@/auth";
import { errText, frDate } from "@/components/pipeline/utils";
import { SmsCompose } from "@/components/steps/SmsCompose";
import { cachedLead, prefetchLead, rememberLead } from "@/components/pipeline/lead/leadCache";
import { Composer } from "@/components/pipeline/lead/Composer";
import { FieldsCard } from "@/components/pipeline/lead/FieldsCard";
import { GuidedScript } from "@/components/pipeline/lead/GuidedScript";
import { availability } from "@/components/pipeline/lead/NextAction";
import { LeadBar } from "@/components/pipeline/lead/LeadBar";
import { LeadHistory } from "@/components/pipeline/lead/LeadHistory";
import { PhoneCard } from "@/components/pipeline/lead/PhoneCard";
import { RemindCalendar } from "@/components/pipeline/lead/RemindCalendar";
import "@/components/pipeline/lead/lead.css";

const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/;
const findEmail = (data) => Object.values(data.fields || {}).map(String).concat(data.lead.description || "").map((v) => v.match(EMAIL)?.[0]).find(Boolean);

// Données de la fiche : affichage immédiat depuis notre base, relecture noCRM en arrière-plan si la copie n'est plus à jour
function useLead(leadId, onClose, onChanged) {
  const [data, setDataRaw] = useState(() => cachedLead(leadId) || null);
  const setData = useCallback((v) => setDataRaw((cur) => { const next = typeof v === "function" ? v(cur) : v; rememberLead(next); return next; }), []);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const edited = useRef(new Set());
  const current = useRef(leadId);
  current.current = leadId;
  const load = useCallback(async () => {
    const mine = () => current.current === leadId; // réponse d'une fiche qu'on a quittée entre-temps : ignorée
    try {
      // déjà affichée depuis le cache si on l'a survolée/ouverte : on relit quand même notre base (≈0,1 s) pour être à jour
      const d = cachedLead(leadId) ? (await api.get(`/pipeline/leads/${leadId}`, { params: { live: false } })).data : await prefetchLead(leadId);
      if (!mine()) return;
      setData((cur) => (cur?.lead?.id === d.lead.id && edited.current.size ? cur : d));
      if (!d.stale) return;
      setRefreshing(true);
      const { data: fresh } = await api.get(`/pipeline/leads/${leadId}`);
      if (!mine()) return;
      const keepMine = (cur) => Object.fromEntries([...edited.current].filter((k) => k in cur.lead).map((k) => [k, cur.lead[k]]));
      setData((cur) => (cur?.lead?.id === fresh.lead.id ? { ...fresh, lead: { ...fresh.lead, ...keepMine(cur) } } : cur));
    } catch (e) { if (mine()) { toast.error(errText(e)); onClose(); } } finally { if (mine()) setRefreshing(false); }
  }, [leadId, onClose, setData]);
  useEffect(() => { setDataRaw(cachedLead(leadId) || null); edited.current = new Set(); if (leadId) load(); }, [leadId, load]);
  const save = async (changes, fields, quiet = false) => {
    setSaving(true);
    Object.keys(changes).forEach((k) => edited.current.add(k));
    try {
      const { data: doc } = await api.patch(`/pipeline/leads/${leadId}`, { ...changes, fields: fields || {} });
      setData((d) => {
        const merged = { ...d.fields, ...(fields || {}) };
        const script = Object.fromEntries(Object.entries(d.script_fields || {}).filter(([k]) => !String(merged[k] || "").trim()));
        return { ...d, lead: { ...d.lead, ...doc, id: d.lead.id }, owner_name: doc.owner_name, fields: merged, script_fields: script };
      });
      onChanged(doc);
      if (!quiet) toast.success(leadId < 0 ? "Enregistré (fiche TEST : rien n'est envoyé vers noCRM)." : "Enregistré (copie noCRM automatique).");
      return true;
    } catch (e) { toast.error(errText(e)); return false; } finally { setSaving(false); }
  };
  // au changement de fiche, jamais les données de la précédente : le cache de la nouvelle, sinon le squelette
  const shown = data?.lead?.id === Number(leadId) ? data : cachedLead(leadId) || null;
  return { data: shown, setData, save, saving, refreshing };
}

const SyncChips = ({ data, refreshing }) => (
  <>
    {refreshing && <span className="pl-sync-chip" data-testid="lead-refreshing">Mise à jour depuis noCRM…</span>}
    {(data.pending_sync || data.offline) && <span className="pl-sync-chip" data-testid="lead-sync-pending" title="Vos changements sont enregistrés ici ; la copie vers noCRM se fait automatiquement dès que noCRM répond.">{data.offline ? "noCRM injoignable · enregistré ici" : "Copie noCRM en cours…"}</span>}
  </>
);

const typing = (e) => e.target.closest?.("input, textarea, select, [contenteditable]") || document.querySelector("[role=dialog][data-state=open]") || e.metaKey || e.ctrlKey || e.altKey;

// Clavier : Échap retour, ←/→ fiche précédente/suivante, A appeler, S SMS, P planifier, C commenter (jamais pendant la saisie)
function useShortcuts({ nav, onClose, onSms, setPlanOpen }) {
  const ref = useRef({});
  ref.current = { nav, onClose, onSms, setPlanOpen };
  useEffect(() => {
    const keys = (e) => {
      const { nav: n, onClose: close, onSms: sms, setPlanOpen: plan } = ref.current;
      if (typing(e) || document.querySelector(".ldx-smodal-back")) return;
      const k = e.key.toLowerCase();
      if (e.key === "Escape" && !document.querySelector(".pop:not(.ldp-peek), [data-radix-popper-content-wrapper]")) close();
      else if (e.key === "ArrowLeft" && n?.prev) n.go(n.prev);
      else if (e.key === "ArrowRight" && n?.next) n.go(n.next);
      else if (k === "a") document.querySelector("[data-testid=lead-bar-call]")?.click();
      else if (k === "s") sms();
      else if (k === "p") plan(true);
      else if (k === "c") { e.preventDefault(); const el = document.getElementById("lead-comment-input"); el?.scrollIntoView({ block: "center" }); el?.focus(); }
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, []);
}

// Fiche opportunité pensée plateau d'appel : bandeau d'action en haut, zone de travail à gauche, contexte à droite
export const LeadPage = ({ leadId, meta, onClose, onChanged, nav }) => {
  const { data, setData, save, saving, refreshing } = useLead(leadId, onClose, onChanged);
  const ref = useRef(null);
  const lead = data?.lead;
  const ready = Boolean(lead);
  const inlinePhone = callMode() === "embedded";
  const [planOpen, setPlanOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(null);
  const { user, setUser } = useAuth();
  const autoNext = user?.auto_next !== false;
  const setAutoNext = (v) => { setUser((u) => ({ ...u, auto_next: v })); api.patch("/auth/me/preferences", { auto_next: v }).catch(() => {}); };
  const loadDay = useCallback(() => api.get("/pipeline/my-day").then(({ data: d }) => setDay(d)).catch(() => {}), []);
  useEffect(() => { loadDay(); }, [loadDay]);
  // Le haut de la fiche s'affiche d'abord ; les blocs lourds (IA, fichiers, tickets, téléphone) suivent à l'image suivante
  const [late, setLate] = useState(false);
  useEffect(() => {
    setLate(false);
    let t;
    const f = requestAnimationFrame(() => { t = setTimeout(() => setLate(true), 0); });
    return () => { cancelAnimationFrame(f); clearTimeout(t); };
  }, [leadId]);
  // lancée après le premier affichage : l'animation ne retarde jamais l'apparition de la fiche
  useEffect(() => {
    if (!ready) return undefined;
    let stop;
    const f = requestAnimationFrame(() => { stop = enterPage(ref.current); });
    return () => { cancelAnimationFrame(f); stop?.(); };
  }, [ready, leadId]);
  const phoneReady = ready && late;
  useEffect(() => {
    if (!phoneReady || !inlinePhone) return undefined;
    mountInline("pl-sheet-phone-frame").catch(() => {});
    return () => unmountInline();
  }, [phoneReady, inlinePhone]);
  const onSms = useCallback(() => setSmsOpen(true), []);
  useShortcuts({ nav, onClose, onSms, setPlanOpen });
  useEffect(() => { [nav?.prev, nav?.next].filter(Boolean).forEach((id) => prefetchLead(id).catch(() => {})); }, [nav?.prev, nav?.next]);

  if (!lead) return <section className="view ldp-view ldx" data-view="customers" data-testid="opportunity-sheet"><LeadSkeleton onBack={onClose} /></section>;
  const steps = [...meta.pipelines, ...(meta.archived_pipelines || [])].find((p) => p.name === lead.pipeline)?.steps || [];
  const items = timelineItems(data.comments, data.communications);
  const email = findEmail(data);
  const hint = availability(data.comments);
  const copyLink = () => navigator.clipboard.writeText(`${window.location.origin}/pipeline?lead=${leadId}`).then(() => toast.success("Lien de l'opportunité copié."));
  // Issue d'appel ou relance planifiée : tout est noté côté serveur (commentaire, relance, étape si la règle est active)
  const runOutcome = async (outcome, extra = {}) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/pipeline/leads/${leadId}/outcome`, { outcome, ...extra });
      setData((d) => ({ ...d, lead: { ...d.lead, ...r.lead, id: d.lead.id, user_id: r.lead.owner_id ?? d.lead.user_id }, comments: [r.comment, ...d.comments] }));
      onChanged(r.lead);
      toast.success(`${r.comment.content.split(" : ")[0]}${r.step_moved ? ` · étape « ${r.lead.step} »` : ""}`);
      loadDay();
      if (autoNext && nav?.next) setTimeout(() => nav.go(nav.next), 450);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const plan = (date, note = "", time = null) => runOutcome("plan", { date, note, ...(time ? { time } : {}) });
  const onComment = (c) => setData((d) => ({
    ...d, comments: [c, ...d.comments], fields: { ...d.fields, ...(c.filled || {}) },
    script_fields: Object.fromEntries(Object.entries(d.script_fields || {}).filter(([k]) => !(c.filled || {})[k])),
  }));
  return (
    <section className="view ldp-view ldx" data-view="customers" ref={ref} data-testid="opportunity-sheet">
      <LeadBar lead={lead} data={data} meta={meta} steps={steps} email={email} saving={saving} save={save} onBack={onClose} nav={nav} onCopy={copyLink} onSms={onSms} onPlan={plan} onCalled={() => setPhoneOpen(true)} planOpen={planOpen} setPlanOpen={setPlanOpen} hint={hint} onOutcome={runOutcome} busy={busy} autoNext={autoNext} setAutoNext={setAutoNext} day={day} sync={<SyncChips data={data} refreshing={refreshing} />} />
      {/* 3 colonnes égales : qui est le client · on agit (script, compte rendu, historique) · téléphone et ce qui vient ensuite */}
      <div className="ldx-cols ldx-cols3">
        <aside className="ldx-left" data-testid="lead-col-left">
          <FieldsCard key={`f${lead.id}-${JSON.stringify(data.fields)}`} lead={lead} data={data} meta={meta} saving={saving} save={save} />
          {late && <section className="card ldx-block"><LeadAttachments leadId={leadId} /></section>}
        </aside>
        <div className="ldx-work" data-testid="lead-col-center">
          {data.profile?.script && <GuidedScript key={`s${leadId}`} leadId={leadId} script={data.profile.script} email={email} onComment={onComment} prefill={data.profile.prefill !== false} fields={{ ...(data.script_fields || {}), ...Object.fromEntries(Object.entries(data.fields || {}).filter(([, v]) => String(v || "").trim())) }} comments={data.comments} />}
          <Composer leadId={leadId} onComment={onComment} />
          <LeadHistory key={leadId} items={items} remindDate={lead.remind_date} onApplyDate={(d) => plan(d)} />
        </div>
        <aside className="ldx-side" data-testid="lead-col-right">
          {inlinePhone && <PhoneCard ready={phoneReady} open={phoneOpen} setOpen={setPhoneOpen} phone={data.phones?.[0]} />}
          <RemindCalendar key={leadId} leadId={leadId} steps={steps} currentStep={lead.step} remindDate={lead.remind_date} items={items} saving={saving} onSetRemind={(d) => plan(d)} />
          {late ? <section className="card ldx-ai" data-testid="lead-ai-card"><LeadAi leadId={leadId} ai={data.ai} setAi={(ai) => setData((d) => ({ ...d, ai }))} onUseDate={(d) => plan(d)} /></section> : <div className="card ldp-skel" style={{ height: 120 }} data-testid="lead-late-loading" />}
          {late && <section className="card ldx-block ldx-tickets"><LeadTickets lead={{ lead_id: leadId, title: lead.title }} /></section>}
        </aside>
      </div>
      <SmsCompose open={smsOpen} onOpenChange={setSmsOpen} phone={data.phones?.[0] || ""} leadId={leadId} onSent={() => toast.success("SMS ajouté à l'historique.")} />
    </section>
  );
};

const LeadSkeleton = ({ onBack }) => (
  <div data-testid="opportunity-sheet-loading">
    <header className="card ldx-bar"><div className="ldx-bar__top"><button type="button" className="ibtn" onClick={onBack} aria-label="Retour au Pipeline" data-testid="lead-page-back">←</button><div className="ldp-skel" style={{ height: 34, width: 320, borderRadius: 10 }} /></div><div className="ldp-skel" style={{ height: 56, borderRadius: 12 }} /></header>
    <div className="ldx-cols ldx-cols3"><aside className="ldx-left"><div className="card ldp-skel" style={{ height: 420 }} /></aside><div className="ldx-work"><div className="card ldp-skel" style={{ height: 130 }} /><div className="card ldp-skel" style={{ height: 380 }} /></div><aside className="ldx-side"><div className="card ldp-skel" style={{ height: 300 }} /></aside></div>
  </div>
);
